import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { container } from "setup/dependency/inversify.config";
import { ClientPortalGlobalSearchResultModel, ClientPortalGlobalSearchResultTypes } from "services/api/cplapi/CPLAPI";
import { RootState } from "setup/redux/RootReducer";
import { IClientPortalGlobalSearchService } from "services/ClientPortalGlobalSearchService";
import { FilterRadioOption } from "cpl-react-common/components/Inputs/FilterRadioSelector";

type GlobalSearchType = {
  SearchTerm: string | undefined;
  Searching: boolean;
  SearchResults: ClientPortalGlobalSearchResultModel[];
  FilteredSearchResults: ClientPortalGlobalSearchResultModel[];
  SearchFilters: FilterRadioOption[];
  SelectedSearchFilter: ClientPortalGlobalSearchResultTypes;
};

const allFilterRadioOption: FilterRadioOption = {
  displayName: "All Results",
  value: ClientPortalGlobalSearchResultTypes.All,
  isDefault: true,
};

const initialState: GlobalSearchType = {
  SearchTerm: undefined,
  Searching: false,
  SearchResults: new Array<ClientPortalGlobalSearchResultModel>(),
  FilteredSearchResults: new Array<ClientPortalGlobalSearchResultModel>(),
  SearchFilters: new Array<FilterRadioOption>(allFilterRadioOption),
  SelectedSearchFilter: ClientPortalGlobalSearchResultTypes.All,
};

export const globalSearchSlice = createSlice({
  name: "GlobalSearch",
  initialState,
  reducers: {
    setSearchFilters: (state) => {
      const filters = new Array<FilterRadioOption>();
      const types = new Array<ClientPortalGlobalSearchResultTypes>();
      filters.push(allFilterRadioOption);
      if (state.SearchResults.length > 0) {
        state.SearchResults.map((result) => {
          if (types.indexOf(result.type!) === -1) {
            filters.push({ displayName: result.label!, value: result.type!, isDefault: false });
            types.push(result.type!);
          }
        });
      }
      state.SearchFilters = filters;
    },
    setSelectedSearchFilter: (state, action) => {
      const selectedSearchFilter: ClientPortalGlobalSearchResultTypes = action.payload;
      state.SearchFilters = state.SearchFilters.map((filter) => {
        if (filter.value == selectedSearchFilter) {
          return { displayName: filter.displayName, value: filter.value, isDefault: true };
        } else if (filter.isDefault) {
          return { displayName: filter.displayName, value: filter.value, isDefault: false };
        } else return filter;
      });
      state.SelectedSearchFilter = selectedSearchFilter;
      globalSearchSlice.caseReducers.setFilteredSearchResults(state);
    },
    resetSelectedSearchFilter: (state) => {
      state.SelectedSearchFilter = ClientPortalGlobalSearchResultTypes.All;
    },
    setFilteredSearchResults: (state) => {
      let filteredResults;
      if (state.SelectedSearchFilter === ClientPortalGlobalSearchResultTypes.All) {
        filteredResults = state.SearchResults;
      } else {
        filteredResults = state.SearchResults.filter((i) => {
          return i.type === state.SelectedSearchFilter;
        });
      }
      state.FilteredSearchResults = filteredResults;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(globalSearch.pending, (state) => {
        state.Searching = true;
      })
      .addCase(globalSearch.rejected, (state) => {
        state.Searching = false;
        state.SearchResults = new Array<ClientPortalGlobalSearchResultModel>();
      })
      .addCase(globalSearch.fulfilled, (state, action) => {
        state.Searching = false;
        state.SearchResults = action.payload;
        globalSearchSlice.caseReducers.resetSelectedSearchFilter(state);
        globalSearchSlice.caseReducers.setSearchFilters(state);
        globalSearchSlice.caseReducers.setFilteredSearchResults(state);
      });
  },
});

export const globalSearch = createAsyncThunk<
  ClientPortalGlobalSearchResultModel[],
  { searchTerm: string },
  { state: RootState }
>("GlobalSearch/globalSearch", async ({ searchTerm }, thunkAPI) => {
  const state = thunkAPI.getState();
  if (searchTerm == null || searchTerm === undefined || searchTerm === "")
    return new Array<ClientPortalGlobalSearchResultModel>();
  if (searchTerm == state.globalSearch.SearchTerm) return state.globalSearch.SearchResults;
  const globalSearchService = container.get<IClientPortalGlobalSearchService>(
    SERVICE_TYPES.ClientPortalGlobalSearchService
  );
  state.globalSearch.SearchTerm == searchTerm;
  return await globalSearchService.globalSearch(searchTerm);
});

export const { setSearchFilters, setSelectedSearchFilter } = globalSearchSlice.actions;

export const selectSearching = (state: RootState) => state.globalSearch.Searching;
export const selectSearchFilters = (state: RootState) => state.globalSearch.SearchFilters;
export const selectFilteredSearchResults = (state: RootState) => state.globalSearch.FilteredSearchResults;

export default globalSearchSlice.reducer;
